/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';

// styles
import {
  StyledCol,
  StyledContainer,
  StyledImage,
  StyledVideo,
  StyledVideoPlayer,
} from './VideoPlayer.styles';

// types
import type { VideoPlayerProps, VideoPlayerStatus } from '.';

// components
import { ContentBox } from './components/ContentBox';

// utils
import { VIDEO_PLAYER_STATUS } from '.';
import {
  addDefaultValuesToMissingPropsValues,
  addVideoPlayerScript,
  getIsVideoPlayerScriptAvailable,
  getVideoPlayer,
  getIsVideoPlayerInitialized,
  removeVideoPlayerControls,
  addVideoPlayer,
} from './VideoPlayer.utils';

const DURATION_TO_WAIT_FOR_VIDEO_PLAYER_INITIALIZATION = 10000; // 10 seconds

export const VideoPlayer = (props: VideoPlayerProps) => {
  const {
    cssID,
    inView,
    subheading,
    heading,
    title: pictureTitle,
    button,
    color,
    fallbackImg,
    loopType,
    hasPreviewId = true,
    position = 'top',
    animation = false,
    emphasize,
    isSection,
    tag,
  } = props;

  const [isVideoPlayerScriptAvailable, setIsVideoPlayerScriptAvailable] = useState<boolean>(false);
  const [isVideoPlayerInitialized, setIsVideoPlayerInitialized] = useState<boolean>(() => {
    return false;
  });
  const [hasVideoReachedEnd, setHasVideoReachedEnd] = useState<boolean>(false);

  const [hasControls, setHasControls] = useState<boolean>(true);

  const [status, setStatus] = useState<VideoPlayerStatus>(VIDEO_PLAYER_STATUS.LOADING);
  const [showPreviewImage, setShowPreviewImage] = useState<boolean>(true);
  const count = useRef<number>(0);
  const videoPlayer = useRef<any>(null);
  const shouldLoop = loopType === 'loop';

  useEffect(() => {
    const shouldShowPreviewImage =
      status === VIDEO_PLAYER_STATUS.LOADING || status === VIDEO_PLAYER_STATUS.ERROR;

    setShowPreviewImage(shouldShowPreviewImage);
  }, [status]);

  // Adds the video player script if needed.
  useEffect(() => addVideoPlayerScript(), []);

  useEffect(() => {
    if (videoPlayer.current && props?.video?.videoId)
      videoPlayer.current.switchVideo(props.video.videoId);
  }, [props?.video?.videoId]);

  useEffect(() => {
    // wait for video player script and video player to be initialized
    if (isVideoPlayerInitialized) return;
    if (count.current > DURATION_TO_WAIT_FOR_VIDEO_PLAYER_INITIALIZATION) return;

    const intervalID = setInterval(() => {
      //Log.info(`⏳ (${count.current}) waiting for video player to be initialized...`);
      // Check script is added and loaded.
      if (!isVideoPlayerScriptAvailable) {
        // Check script was not available.
        const wasAddingSuccessful = getIsVideoPlayerScriptAvailable();
        if (wasAddingSuccessful) {
          // Script is available (added and loaded)
          // Add the video player.
          setIsVideoPlayerScriptAvailable(true);
          const videoPlayerProps = addDefaultValuesToMissingPropsValues(props);
          addVideoPlayer(videoPlayerProps);
        }
      }

      // Check video player was initialized.
      if (getIsVideoPlayerInitialized(cssID)) {
        setIsVideoPlayerInitialized(true);
        clearInterval(intervalID);
      }

      if (count.current > DURATION_TO_WAIT_FOR_VIDEO_PLAYER_INITIALIZATION) {
        setIsVideoPlayerInitialized(false);
        clearInterval(intervalID);
      }

      count.current += 1000;
    }, 1000);

    return () => clearInterval(intervalID);
  });

  useEffect(() => {
    if (!isVideoPlayerScriptAvailable || !isVideoPlayerInitialized) {
      setStatus(VIDEO_PLAYER_STATUS.LOADING);
      setShowPreviewImage(true);
      return;
    }

    if (isVideoPlayerInitialized && isVideoPlayerScriptAvailable) {
      setStatus(VIDEO_PLAYER_STATUS.DONE);
      setShowPreviewImage(false);
      return;
    }

    setStatus(VIDEO_PLAYER_STATUS.DONE);
    setShowPreviewImage(false);
  }, [isVideoPlayerInitialized, isVideoPlayerScriptAvailable]);

  const initVideoPlayer = () => {
    if (!videoPlayer.current) {
      const player = getVideoPlayer(cssID);
      if (!player) {
        throw new Error('Video player not initialized');
      }
      videoPlayer.current = player;
    }
  };

  const configureVideoPlayer = () => {
    if (hasControls) {
      removeVideoPlayerControls(cssID);
      setHasControls(false);
    }

    // add event handlers
    if (shouldLoop) {
      const loopEventHandler = () => videoPlayer.current.play();
      inView
        ? videoPlayer.current.registerEventListener('ended', loopEventHandler)
        : videoPlayer.current.unregisterEventListener('ended', loopEventHandler);
    }

    if (!shouldLoop && !hasVideoReachedEnd) {
      const playOnlyOnceEndedEventHandler = () => {
        const _hasReachedEndOfVideo =
          videoPlayer.current.getDuration() - videoPlayer.current.getCurrentTime() < 0.5;

        if (_hasReachedEndOfVideo) {
          setHasVideoReachedEnd(true);
          setShowPreviewImage(true);
          return;
        }
        videoPlayer.current.play();
      };
      inView
        ? videoPlayer.current.registerEventListener('ended', playOnlyOnceEndedEventHandler)
        : videoPlayer.current.unregisterEventListener('ended', playOnlyOnceEndedEventHandler);
    }
  };

  const startVideo = () => {
    if (inView) {
      videoPlayer.current.play();
    }

    if (!inView) {
      videoPlayer.current.pause();
    }
  };

  useEffect(() => {
    // play / pause video in player
    if (status !== VIDEO_PLAYER_STATUS.DONE) return;

    initVideoPlayer();
    configureVideoPlayer();
    startVideo();
  }, [status, inView, shouldLoop, cssID, hasVideoReachedEnd, hasControls]);

  return (
    <StyledVideoPlayer className="video-player-mi24" title={pictureTitle}>
      <StyledContainer>
        <StyledCol>
          <ContentBox
            button={button}
            heading={heading}
            subheading={subheading}
            color={color}
            position={position}
            hasPreviewId={hasPreviewId}
            animation={animation}
            emphasize={emphasize}
            isSection={isSection}
            tag={tag}
          />
        </StyledCol>
      </StyledContainer>

      {showPreviewImage && (
        <StyledImage
          className="video-player-mi24__preview-image"
          src={fallbackImg}
          alt={pictureTitle}
        />
      )}

      <StyledVideo
        className="video-player-mi24__video-player"
        id={cssID}
        showVideo={!showPreviewImage}
      />
    </StyledVideoPlayer>
  );
};
